import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Timer, CalendarDays, LogIn, Share2, ChevronsUp, Bell } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { nextTier } from "@/game/config";
import { shareScoreCard, renderScoreCardUrl } from "@/lib/shareCard";
import { formatTime } from "@/lib/format";
import WeeklyGiftsCta from "@/components/WeeklyGiftsCta";
import RewardModal from "@/components/RewardModal";
import BadgeModal from "@/components/BadgeModal";

const CORDOBA_PTS = 75;
const PB_KEY = "nicabros_best";
const TIME_KEY = "nicabros_besttime";
const DIFF_BADGE = { facil: { label: "EASY", color: "#4CAF50" }, normal: { label: "NORMAL", color: "#0067C6" }, extremo: { label: "☠️ EXTREMO", color: "#E91E63" } };

// Win / Game-Over overlay with score submission.
export default function ResultOverlay({ win, result, character, level, onReplay, onMenu, onNext, onStepUp, hasNext, onAuth, onPlayManagua, cordobas = 0, difficulty = "normal", mode = "normal", dailyDate = "", challenge = null }) {
  const { t, lang } = useLang();
  const { user, refreshUser } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const [reward, setReward] = useState(null);
  const [challengeBadge, setChallengeBadge] = useState(null);
  const [email, setEmail] = useState("");
  const [leadSaving, setLeadSaving] = useState(false);
  const [leadDone, setLeadDone] = useState(() => !!localStorage.getItem("nicabros_score_email"));

  const cordobaBonus = cordobas * CORDOBA_PTS;
  const totalScore = result.score + cordobaBonus;
  const timeMs = result.timeMs || 0;
  const badge = DIFF_BADGE[difficulty] || DIFF_BADGE.normal;
  const stepTier = win ? nextTier(difficulty) : null;

  // Build a small preview of the shareable score card (every level).
  useEffect(() => {
    let url = null;
    renderScoreCardUrl({
      score: totalScore,
      characterName: character.name,
      levelName: level.name,
      charColor: character.colors?.accent || "#FFEB3B",
      lang,
      levelIndex: level.index,
      charId: character.id,
      diffId: difficulty,
      playerName: user?.username || "",
    }).then((u) => { url = u; setCardUrl(u); });
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Personal best score + time per mode/level/difficulty (localStorage). Once on mount.
  const { isNewBest, prevBest, bestTime, isNewTime } = useMemo(() => {
    const key = `${mode}_${level.index}_${difficulty}`;
    let bests = {};
    try { bests = JSON.parse(localStorage.getItem(PB_KEY) || "{}"); } catch (e) { bests = {}; }
    const prev = bests[key] || 0;
    const newBest = totalScore > prev;
    if (newBest) { bests[key] = totalScore; localStorage.setItem(PB_KEY, JSON.stringify(bests)); }

    let bTime = 0, newTime = false;
    if (win) {
      let times = {};
      try { times = JSON.parse(localStorage.getItem(TIME_KEY) || "{}"); } catch (e) { times = {}; }
      const prevT = times[key] || 0;
      if (!prevT || timeMs < prevT) { times[key] = timeMs; localStorage.setItem(TIME_KEY, JSON.stringify(times)); newTime = true; bTime = timeMs; }
      else bTime = prevT;
    }
    return { isNewBest: newBest, prevBest: prev, bestTime: bTime, isNewTime: newTime };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doShareWin = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const res = await shareScoreCard({
        score: totalScore,
        characterName: character.name,
        levelName: level.name,
        charColor: character.colors?.accent || "#FFEB3B",
        lang,
        levelIndex: level.index,
        charId: character.id,
        diffId: difficulty,
        playerName: user?.username || "",
      });
      if (res === "downloaded") toast.success(t("cardSaved"));
      else if (res === "failed") toast.error(t("cardShareFail"));
    } finally {
      setSharing(false);
    }
  };

  const submit = async () => {
    if (saving || submitted || !user) return;
    setSaving(true);
    try {
      await api.saveScore({
        player_name: user.username,
        character: character.name,
        level_index: level.index,
        level_name: level.name,
        score: totalScore,
        cordobas,
        completed: win,
        difficulty,
        time_ms: timeMs,
        mode,
        daily_date: mode === "daily" ? dailyDate : "",
      });
      setSubmitted(true);
      // Difficulty progression: record the tier clear & award first-clear coins.
      if (win && mode === "normal") {
        try {
          const r = await api.completeLevelTier({ level_index: level.index, difficulty, character_id: character?.id });
          if (r.data?.awarded > 0) toast.success(t("coinsEarned")(r.data.awarded));
          if (r.data?.unlocked_character) setReward(r.data.unlocked_character);
          if (r.data?.badge_awarded) setChallengeBadge(r.data.badge_awarded);
          if (r.data?.awarded > 0 || r.data?.unlocked_character || r.data?.badge_awarded) refreshUser?.();
        } catch (e) { /* noop */ }
      }
    } catch (e) {
      setSubmitted(true);
    } finally {
      setSaving(false);
    }
  };

  // Not-signed-up players: capture just an email so we still get the lead (and
  // can send Spanish daily reminders) even when they don't create a full profile.
  const captureEmail = async () => {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error(t("scoreEmailInvalid")); return; }
    setLeadSaving(true);
    try {
      await api.captureLead({ email: e, source: "score_prompt" });
      localStorage.setItem("nicabros_score_email", e);
      setLeadDone(true);
      toast.success(t("scoreEmailThanks"));
    } catch (_) {
      toast.error(t("scoreEmailFail"));
    } finally {
      setLeadSaving(false);
    }
  };

  // Signed-up players get their score saved automatically — no button tap needed.
  useEffect(() => {
    if (user && !submitted && !saving) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="absolute inset-0 z-[60] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-3" data-testid="result-overlay">
      <div className="pixel-card bg-[#141414] w-full max-w-md text-center pop-in max-h-full my-auto flex flex-col overflow-hidden" data-testid={win ? "win-modal" : "game-over-modal"}>
        {/* Scrollable details */}
        <div className="overflow-y-auto p-4 md:p-6 pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
        {mode === "daily" && (
          <p className="font-pixel text-[10px] text-[#FFB300] mb-2 flex items-center justify-center gap-1" data-testid="daily-result-tag"><CalendarDays size={12} strokeWidth={2.5} /> {t("dailyChallenge")}</p>
        )}
        <h2 className="font-pixel text-xl md:text-4xl pixel-stroke" style={{ color: win ? "#4CAF50" : "#FF5252" }}>
          {win ? t("win") : t("gameOver")}
        </h2>
        <p className="font-vt text-xl md:text-2xl text-white mt-2">{level.name}</p>
        <span className="inline-block font-pixel text-[9px] text-white px-2 py-1 rounded-md mt-2" style={{ backgroundColor: badge.color }} data-testid="result-diff-badge">{badge.label}</span>

        {challenge && challenge.score > 0 && (
          <div
            data-testid="challenge-result-banner"
            className="mt-3 px-3 py-2 rounded-md font-vt text-lg leading-tight"
            style={{ backgroundColor: totalScore > challenge.score ? "rgba(76,175,80,0.18)" : "rgba(233,30,99,0.18)", border: `2px solid ${totalScore > challenge.score ? "#4CAF50" : "#E91E63"}`, color: "#fff" }}
          >
            {totalScore > challenge.score
              ? t("challengeYouWon")(challenge.name || "?")
              : t("challengeYouLost")(challenge.name || "?", challenge.score.toLocaleString())}
          </div>
        )}

        <div className="my-3 py-2.5 border-y-2 border-white/15">
          {isNewBest ? (
            <p className="font-pixel text-xs text-[#FFEB3B] blink mb-2" data-testid="new-best-badge">🏆 {t("newBest")}</p>
          ) : (
            prevBest > 0 && <p className="font-vt text-lg text-white/50 mb-1" data-testid="prev-best">{t("best")}: {prevBest}</p>
          )}
          <p className="font-pixel text-[10px] text-[#FFEB3B]">{t("finalScore")}</p>
          <p className="font-pixel text-3xl md:text-4xl text-white mt-2" data-testid="final-score">{String(totalScore).padStart(6, "0")}</p>
          <p className="font-vt text-xl text-white/70 mt-2">🍺 {t("beersLabel")}: {result.beers}</p>
          {cordobas > 0 && (
            <p className="font-vt text-xl text-[#f2b701] mt-1" data-testid="result-cordobas">🪙 C$ {cordobas} → {t("bonusBonus")} +{cordobaBonus}</p>
          )}
          {win && (
            <p className="font-vt text-xl text-white/80 mt-2 flex items-center justify-center gap-1" data-testid="result-time">
              <Timer size={14} strokeWidth={2.5} /> {formatTime(timeMs)}
              {bestTime ? <span className="text-white/40"> · {t("best")}: {formatTime(bestTime)}</span> : null}
            </p>
          )}
          {isNewTime && <p className="font-pixel text-[10px] text-[#00e5ff] blink mt-1" data-testid="new-record-badge">⏱️ {t("newRecord")}</p>}
        </div>

        {/* Share your score — prominent, every level */}
        <div className="flex items-center gap-3 my-3 p-2 rounded-xl bg-[#E91E63]/10 ring-2 ring-[#E91E63]/40">
          {cardUrl && (
            <img src={cardUrl} alt="score card" data-testid="score-card-preview" className="w-16 h-16 rounded-lg object-cover ring-1 ring-white/20 shrink-0" />
          )}
          <button data-testid="share-win-btn" onClick={doShareWin} disabled={sharing} className="pixel-btn bg-[#E91E63] text-white flex-1 px-4 py-3.5 text-sm flex items-center justify-center gap-2">
            <Share2 size={18} strokeWidth={2.5} /> {sharing ? t("saving") : t("shareScore")}
          </button>
        </div>

        <WeeklyGiftsCta prefix="result" onSignUp={onAuth} className="mt-3" />
        </div>

        {/* Pinned footer — always-visible Save + actions so they never fall below the fold */}
        <div className="shrink-0 px-4 py-3 border-t-2 border-white/10 bg-[#141414] space-y-2">
        {!submitted ? (
          user ? (
            <div className="flex flex-col items-center gap-2">
              <p className="font-vt text-base text-white/70" data-testid="save-as-name">{t("saveAs")} <span className="text-[#FFEB3B]">{user.username}</span></p>
              <button data-testid="submit-score-btn" onClick={submit} disabled={saving} className="pixel-btn bg-[#0067C6] text-white px-6 py-2.5 text-xs w-full">
                {saving ? t("saving") : t("saveScore")}
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 w-full">
              {!leadDone ? (
                <>
                  <p className="font-vt text-base text-white/80 leading-tight">{t("scoreEmailCta")}</p>
                  <div className="flex gap-2 w-full">
                    <input
                      data-testid="result-email-input"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") captureEmail(); }}
                      placeholder={t("scoreEmailPlaceholder")}
                      className="flex-1 min-w-0 bg-black/40 border-2 border-white/20 rounded-md px-3 py-2.5 font-vt text-lg text-white placeholder-white/40 focus:outline-none focus:border-[#4CAF50]"
                    />
                    <button data-testid="result-email-save-btn" onClick={captureEmail} disabled={leadSaving} className="pixel-btn bg-[#4CAF50] text-white px-4 py-2.5 text-[11px] whitespace-nowrap flex items-center gap-1.5">
                      <Bell size={14} strokeWidth={2.5} /> {leadSaving ? t("saving") : t("scoreEmailBtn")}
                    </button>
                  </div>
                  <button data-testid="result-create-profile-btn" onClick={onAuth} className="font-vt text-sm text-white/55 underline underline-offset-2 hover:text-white/85">
                    {t("orCreateProfile")}
                  </button>
                </>
              ) : (
                <>
                  <p className="font-vt text-lg text-[#4CAF50]" data-testid="result-email-thanks">{t("scoreEmailThanks")}</p>
                  <button data-testid="result-create-profile-btn" onClick={onAuth} className="pixel-btn bg-[#0067C6] text-white px-6 py-2.5 text-xs w-full flex items-center justify-center gap-2">
                    <LogIn size={16} strokeWidth={2.5} /> {t("createProfileSave")}
                  </button>
                </>
              )}
            </div>
          )
        ) : (
          <p className="font-vt text-xl text-[#4CAF50]" data-testid="score-saved-msg">{t("saved")}</p>
        )}

        <div className="grid grid-cols-2 gap-2">
          {win && mode === "normal" && stepTier && (
            <button data-testid="step-up-btn" onClick={() => onStepUp?.(stepTier)} className="pixel-btn text-white px-4 py-2.5 text-[11px] col-span-2 flex items-center justify-center gap-1.5" style={{ backgroundColor: DIFF_BADGE[stepTier].color }}>
              <ChevronsUp size={15} strokeWidth={2.5} /> {t("playOnTier")(DIFF_BADGE[stepTier].label)}
            </button>
          )}
          {win && hasNext && (
            <button data-testid="next-level-btn" onClick={onNext} className="pixel-btn bg-[#4CAF50] text-white px-4 py-2.5 text-[11px]">
              {t("nextLevel")}
            </button>
          )}
          {onPlayManagua && (
            <button data-testid="play-managua-btn" onClick={onPlayManagua} className="pixel-btn bg-[#4CAF50] text-white px-4 py-2.5 text-[11px]">
              {t("playManagua")}
            </button>
          )}
          <button data-testid="replay-btn" onClick={onReplay} className="pixel-btn bg-[#FF5722] text-white px-4 py-2.5 text-[11px]">
            {t("retry")}
          </button>
          <button data-testid="result-menu-btn" onClick={onMenu} className="pixel-btn bg-[#222] text-white px-4 py-2.5 text-[11px]">
            {t("menu")}
          </button>
        </div>
        </div>
      </div>
      {reward && <RewardModal charId={reward.id} charName={reward.name} onClose={() => setReward(null)} />}
      {challengeBadge && <BadgeModal badge={challengeBadge} onClose={() => setChallengeBadge(null)} />}
    </div>
  );
}
